/**
 * Somebody's time-in-lieu history (DESIGN-toil.md screen 7): one line per claim, decision,
 * booking, correction and expiry, newest first, with a running balance — so the balance can
 * always be explained. Nothing here is ever edited (G); a correction is its own line.
 */
import React from 'react';
import { formatDateGB } from '../../lib/dates';

const CHIP = {
  pending: ['Waiting', 'bg-amber-500/15 text-amber-600'],
  approved: ['Approved', 'bg-emerald-500/15 text-emerald-600'],
  declined: ['Declined', 'bg-red-500/15 text-red-600'],
  withdrawn: ['Withdrawn', 'bg-secondary text-muted-foreground'],
  cancelled: ['Cancelled', 'bg-secondary text-muted-foreground'],
};

export default function ToilHistory({ lines, onWithdraw, withdrawableBy }) {
  if (!lines || lines.length === 0) {
    return <p className="text-sm text-muted-foreground">Nothing yet.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="toil-history">
        <thead>
          <tr className="text-xs text-muted-foreground border-b border-border">
            <th className="text-left font-semibold py-2 pr-2">Date</th>
            <th className="text-left font-semibold px-2">What happened</th>
            <th className="text-left font-semibold px-2">By</th>
            <th className="text-left font-semibold px-2 whitespace-nowrap">Use by</th>
            <th className="text-right font-semibold px-2">Change</th>
            <th className="text-right font-semibold pl-2">Balance</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => {
            const chip = (l.kind === 'claim' || l.kind === 'booking') && l.status !== 'approved' ? CHIP[l.status] : null;
            const showApproved = l.kind === 'booking' && l.status === 'approved';
            return (
              <tr key={`${l.kind}-${l.id}`} className="border-b border-border/50 align-top">
                <td className="py-2 pr-2 whitespace-nowrap">{formatDateGB(l.date)}</td>
                <td className="px-2">
                  {l.text}
                  {chip && <span className={`ml-2 text-[11px] font-semibold rounded-full px-2 py-0.5 ${chip[1]}`}>{chip[0]}</span>}
                  {showApproved && <span className={`ml-2 text-[11px] font-semibold rounded-full px-2 py-0.5 ${CHIP.approved[1]}`}>Approved</span>}
                  {l.decline_note && <span className="block text-xs text-muted-foreground mt-0.5">Note: {l.decline_note}</span>}
                  {onWithdraw && l.kind === 'claim' && l.status === 'pending' && withdrawableBy && (
                    <button type="button" className="ml-2 text-xs text-primary underline" onClick={() => onWithdraw(l.id)}
                      data-testid="toil-withdraw">Withdraw</button>
                  )}
                </td>
                <td className="px-2 whitespace-nowrap">{l.by}</td>
                <td className="px-2 whitespace-nowrap">{l.use_by ? formatDateGB(l.use_by) : (l.doesnt_expire ? "Doesn't expire" : '')}</td>
                <td className={`px-2 text-right whitespace-nowrap font-semibold ${
                  l.change > 0 ? 'text-emerald-600' : l.change < 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                  {l.change_label || '—'}
                </td>
                <td className={`pl-2 text-right whitespace-nowrap ${l.balance < 0 ? 'text-red-600 font-semibold' : ''}`}>{l.balance_label}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
